import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import PageSizeDialog from '../components/ui/PageSizeDialog';
import { buildCategoryFilters } from '../data/kathaData';
import { galleryApi } from '../api/api';
import { youtubeThumb } from '../utils/youtube';
import useHistoryBackClose from '../hooks/useHistoryBackClose';

const GALLERY_CATEGORY_ORDER = ['temple', 'satsang', 'guruji', 'meditation', 'festivals', 'events'];

// Gallery data is now loaded from the database via PHP API.
// Static placeholder kept for reference only.
const _STATIC_GALLERY_PLACEHOLDER = [
  { id: 1, src: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", title: "Temple Architecture", category: "temple", className: "col-span-2 row-span-2" },
  { id: 2, src: "https://images.unsplash.com/photo-1545558014-8692077e9b5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Sacred Shrine", category: "temple", className: "aspect-square" },
  { id: 3, src: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Events Gathering", category: "satsang", className: "aspect-square" },
  { id: 4, src: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Spiritual Discourse", category: "satsang", className: "aspect-square" },
  { id: 5, src: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Guruji Teaching", category: "guruji", className: "aspect-square" },
  { id: 6, src: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Meditation Session", category: "meditation", className: "row-span-2" },
  { id: 7, src: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Festival Celebration", category: "festivals", className: "aspect-square" },
  { id: 8, src: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Diwali Celebration", category: "festivals", className: "aspect-square" },
  { id: 9, src: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Special Event", category: "events", className: "aspect-square" },
  { id: 10, src: "https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Peaceful Meditation", category: "meditation", className: "aspect-square" },
  { id: 11, src: "https://images.unsplash.com/photo-1540979388789-6cee28a1cdc9?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Evening Events", category: "satsang", className: "aspect-square" },
  { id: 12, src: "https://images.unsplash.com/photo-1564507592333-c60657eea523?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Temple Interior", category: "temple", className: "aspect-square" },
  { id: 13, src: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", title: "Events Video", category: "satsang", className: "col-span-2 aspect-video", isVideo: true, duration: "15:30", youtubeId: "AGjx27_87iY" },
  { id: 14, src: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Guruji Teaching Video", category: "guruji", className: "aspect-square", isVideo: true, duration: "8:45", youtubeId: "AGjx27_87iY" },
  { id: 15, src: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Festival Video", category: "festivals", className: "aspect-square", isVideo: true, duration: "12:20", youtubeId: "AGjx27_87iY" },
  { id: 16, src: "https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Meditation Video", category: "meditation", className: "aspect-square", isVideo: true, duration: "20:15", youtubeId: "AGjx27_87iY" },
  { id: 17, src: "https://images.unsplash.com/photo-1545558014-8692077e9b5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Temple Ceremony Video", category: "temple", className: "aspect-square", isVideo: true, duration: "6:30", youtubeId: "AGjx27_87iY" },
  { id: 18, src: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Temple Entrance", category: "temple", className: "aspect-square" },
  { id: 19, src: "https://images.unsplash.com/photo-1564507592333-c60657eea523?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Sacred Hall", category: "temple", className: "aspect-square" },
  { id: 20, src: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Community Events", category: "satsang", className: "aspect-square" },
  { id: 21, src: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Events Discourse Video", category: "satsang", className: "aspect-square", isVideo: true, duration: "18:45", youtubeId: "AGjx27_87iY" },
  { id: 22, src: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Guruji Blessing", category: "guruji", className: "row-span-2" },
  { id: 23, src: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Guruji with Devotees", category: "guruji", className: "aspect-square" },
  { id: 24, src: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Holi Celebration", category: "festivals", className: "aspect-square" },
  { id: 25, src: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", title: "Diwali Video", category: "festivals", className: "col-span-2 aspect-video", isVideo: true, duration: "25:30", youtubeId: "AGjx27_87iY" },
  { id: 26, src: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Morning Meditation", category: "meditation", className: "aspect-square" },
  { id: 27, src: "https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Silent Meditation Video", category: "meditation", className: "aspect-square", isVideo: true, duration: "45:00", youtubeId: "AGjx27_87iY" },
  { id: 28, src: "https://images.unsplash.com/photo-1540979388789-6cee28a1cdc9?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Special Ceremony", category: "events", className: "aspect-square" },
  { id: 29, src: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Annual Gathering Video", category: "events", className: "aspect-square", isVideo: true, duration: "32:15", youtubeId: "AGjx27_87iY" },
  { id: 30, src: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Temple Bells", category: "temple", className: "aspect-square" },
  { id: 31, src: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Evening Events Video", category: "satsang", className: "aspect-square", isVideo: true, duration: "28:20", youtubeId: "AGjx27_87iY" },
  { id: 32, src: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Janmashtami", category: "festivals", className: "aspect-square" },
  { id: 33, src: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80", title: "Group Meditation", category: "meditation", className: "aspect-square" }
];

const PAGE_SIZE_OPTIONS = [12, 24, 36, 48];

const Gallery = () => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [currentPage, setCurrentPage] = useState(1);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  // API-fetched gallery items
  const [galleryItems, setGalleryItems] = useState([]);
  const [apiLoading,   setApiLoading]   = useState(true);
  const [apiError,     setApiError]     = useState('');

  useEffect(() => {
    setApiLoading(true);
    galleryApi.getAll()
      .then((res) => {
        if (res.success) {
          setGalleryItems((res.data || []).map((item) => ({
            ...item,
            category: (item.category || '').trim().toLowerCase(),
            isVideo: !!(item.isVideo === true || item.isVideo === 1 || item.isVideo === '1'),
            src: item.src || youtubeThumb(item.youtubeId),
            className: item.className || 'aspect-square',
          })));
        } else setApiError(t('galleryPage.load_error'));
      })
      .catch(() => setApiError(t('galleryPage.load_error')))
      .finally(() => setApiLoading(false));
  }, [t]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showPageSizeDialog, setShowPageSizeDialog] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;

  const categoryLabels = useMemo(() => ({
    temple: t('galleryPage.filters.temple'),
    satsang: t('galleryPage.filters.satsang'),
    guruji: t('galleryPage.filters.guruji'),
    meditation: t('galleryPage.filters.meditation'),
    festivals: t('galleryPage.filters.festivals'),
    events: t('galleryPage.filters.events'),
  }), [t]);

  // Category chips derived from categories actually present in loaded gallery data
  const categoryFilters = useMemo(
    () => buildCategoryFilters(galleryItems, {
      allLabel: t('galleryPage.all'),
      labels: categoryLabels,
      order: GALLERY_CATEGORY_ORDER,
    }),
    [galleryItems, t, categoryLabels]
  );

  useEffect(() => {
    if (activeFilter !== 'all' && !categoryFilters.some((f) => f.val === activeFilter)) {
      setActiveFilter('all');
    }
  }, [activeFilter, categoryFilters]);

  // Filter items
  const filteredItems = useMemo(() => {
    if (activeFilter === 'all') {
      return galleryItems;
    }
    return galleryItems.filter(item => item.category === activeFilter);
  }, [activeFilter, galleryItems]);

  // Paginated items
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    return filteredItems.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredItems, currentPage, itemsPerPage]);

  // Reset to page 1 on filter or limit change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, itemsPerPage]);

  const handleItemClick = (item, index) => {
    if (item.isVideo) {
      setPlayingVideo(item);
    } else {
      setLightboxImage(item);
      setLightboxIndex(index);
    }
  };

  const closeLightbox = useCallback(() => setLightboxImage(null), []);
  const closeVideo = useCallback(() => setPlayingVideo(null), []);

  useHistoryBackClose(!!lightboxImage, closeLightbox, 'galleryLightbox');
  useHistoryBackClose(!!playingVideo, closeVideo, 'galleryVideo');

  const nextImage = () => {
    const nextIdx = (lightboxIndex + 1) % paginatedItems.length;
    setLightboxIndex(nextIdx);
    setLightboxImage(paginatedItems[nextIdx]);
  };

  const prevImage = () => {
    const prevIdx = (lightboxIndex - 1 + paginatedItems.length) % paginatedItems.length;
    setLightboxIndex(prevIdx);
    setLightboxImage(paginatedItems[prevIdx]);
  };

  return (
    <>
      <Helmet>
        <title>{t('galleryPage.meta.title')}</title>
        <meta name="description" content={t('galleryPage.meta.description')} />
        <link rel="canonical" href="https://www.shreeramranujadham.org/gallery" />
        <meta property="og:title" content={t('galleryPage.meta.title')} />
        <meta property="og:description" content={t('galleryPage.meta.description')} />
        <meta property="og:url" content="https://www.shreeramranujadham.org/gallery" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.shreeramranujadham.org/logo.jpg" />
      </Helmet>

      {/* ── Page Header ─────────────────────────── */}
      <section
        className="pt-24 pb-6 md:pt-28 md:pb-16 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 50%, #7c3aed 100%)' }}
      >
        {/* Dark overlay for better text contrast */}
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>

        {/* Background Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-20 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-20 rounded-full translate-y-40 -translate-x-40"></div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-400 bg-opacity-10 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-yellow-400 bg-opacity-10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="mb-2 md:mb-8">
            <div className="inline-flex items-center bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3 mb-6">
              <i className="fas fa-camera text-yellow-300 mr-3 text-xl"></i>
              <span className="text-white font-semibold">{t('galleryPage.badge')}</span>
            </div>
          </div>
          <h1 className="text-2xl md:text-7xl lg:text-8xl font-bold text-white mb-3 md:mb-8 leading-tight drop-shadow-lg">
            {t('galleryPage.title_1')}<br />
            <span className="text-yellow-300">{t('galleryPage.title_2')}</span>
          </h1>
          <p className="text-xs md:text-xl text-white max-w-3xl mx-auto leading-relaxed mb-8 md:mb-12 drop-shadow-md font-medium">
            {t('galleryPage.subtitle')}
          </p>
        </div>
      </section>

      {/* ── Photo Gallery + Filters ───────────────────── */}
      <section id="photo-gallery" className="pt-8 pb-14 md:pt-12 md:pb-20 bg-white">
        <div className="container mx-auto px-4">
          {/* Filters — derived from categories present in gallery items */}
          <div className="overflow-x-auto scrollbar-hide mb-6 md:mb-8">
            <div className="flex flex-nowrap justify-center gap-2 min-w-full w-max mx-auto">
              {categoryFilters.map((f) => {
                const isActive = activeFilter === f.val;
                return (
                  <button
                    key={f.val}
                    type="button"
                    onClick={() => setActiveFilter(f.val)}
                    className={`gallery-filter flex-shrink-0 px-4 py-1.5 md:px-5 md:py-2 rounded-lg text-base font-medium transition-colors border ${
                      isActive
                        ? 'bg-orange-500 text-white border-orange-500 active'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300'
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {apiLoading && (
            <div className="text-center py-20">
              <i className="fas fa-spinner fa-spin text-orange-500 text-3xl mb-3"></i>
              <p className="text-gray-500">{t('galleryPage.loading')}</p>
            </div>
          )}
          {!apiLoading && apiError && (
            <div className="text-center py-20">
              <i className="fas fa-exclamation-circle text-red-400 text-3xl mb-3"></i>
              <p className="text-gray-600 font-medium">{apiError}</p>
            </div>
          )}
          {!apiLoading && !apiError && galleryItems.length === 0 && (
            <div className="text-center py-20">
              <i className="fas fa-images text-gray-300 text-4xl mb-3"></i>
              <p className="text-gray-500 font-medium">{t('galleryPage.empty')}</p>
            </div>
          )}
          {!apiLoading && !apiError && galleryItems.length > 0 && filteredItems.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500">{t('galleryPage.empty_category')}</p>
            </div>
          )}
          <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2${apiLoading || apiError || filteredItems.length === 0 ? ' hidden' : ''}`} id="photo-gallery-grid">
            {paginatedItems.map((item, idx) => (
              <div
                key={item.id}
                onClick={() => handleItemClick(item, idx)}
                className={`gallery-item group relative overflow-hidden rounded-lg cursor-pointer ${item.className || 'aspect-square'}`}
              >
                <img
                  src={item.src}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-350"
                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1545558014-8692077e9b5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' }}
                />

                {item.isVideo ? (
                  <>
                    <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                      <i className="fas fa-play-circle text-white text-4xl opacity-80 group-hover:opacity-100 transition-opacity duration-300"></i>
                    </div>
                    <div className="absolute bottom-2 right-2">
                      <span className="bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">{item.duration}</span>
                    </div>
                  </>
                ) : (
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                    <i className="fas fa-search-plus text-white text-xl md:text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="py-6 md:py-8 border-t border-orange-100 mt-12">
              {isMobile ? (
                // Mobile Pagination
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <span className="text-xs text-gray-500">{t('galleryPage.per_page')}</span>
                    <button
                      type="button"
                      onClick={() => setShowPageSizeDialog(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:border-orange-400 hover:text-orange-600 transition-colors"
                    >
                      {itemsPerPage}
                      <i className="fas fa-chevron-down text-xs text-gray-400"></i>
                    </button>
                  </div>
                  <div className="flex items-center justify-between w-full">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`flex items-center text-gray-600 hover:text-orange-600 font-medium px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors text-sm ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <i className="fas fa-chevron-left mr-1"></i>{t('galleryPage.previous')}
                    </button>
                    <span className="text-gray-600 text-sm font-medium">{t('galleryPage.page_of', { current: currentPage, total: totalPages })}</span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={`flex items-center text-gray-600 hover:text-orange-600 font-medium px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors text-sm ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      {t('galleryPage.next')}<i className="fas fa-chevron-right ml-1"></i>
                    </button>
                  </div>
                </div>
              ) : (
                // Desktop Pagination
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={`flex items-center text-gray-600 hover:text-orange-600 font-medium px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <i className="fas fa-chevron-left mr-2"></i>{t('galleryPage.previous_gallery')}
                  </button>

                  <div className="flex items-center gap-4">
                    <span className="text-gray-600 text-sm font-medium">{t('galleryPage.page_of', { current: currentPage, total: totalPages })}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{t('galleryPage.per_page')}</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => setItemsPerPage(parseInt(e.target.value, 10))}
                        className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-orange-500 bg-white"
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className={`flex items-center text-gray-600 hover:text-orange-600 font-medium px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    {t('galleryPage.next_gallery')}<i className="fas fa-chevron-right mr-2"></i>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <PageSizeDialog
        open={showPageSizeDialog}
        onClose={() => setShowPageSizeDialog(false)}
        value={itemsPerPage}
        onChange={setItemsPerPage}
        options={PAGE_SIZE_OPTIONS}
        title={t('galleryPage.page_size_title')}
        optionSuffix={t('galleryPage.page_size_suffix')}
      />

      {/* ── Photo Lightbox Modal ────────────────────── */}
      {lightboxImage && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="max-w-4xl w-full mx-4 relative" onClick={e => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white text-3xl hover:text-orange-400 transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>

            {/* Prev Image */}
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-3xl bg-black bg-opacity-40 hover:bg-opacity-65 w-12 h-12 rounded-full flex items-center justify-center transition-all z-20"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            {/* Image */}
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title}
              className="w-full rounded-xl max-h-[85vh] object-contain mx-auto"
            />

            {/* Next Image */}
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-3xl bg-black bg-opacity-40 hover:bg-opacity-65 w-12 h-12 rounded-full flex items-center justify-center transition-all z-20"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* ── Video Lightbox Modal ────────────────────── */}
      {playingVideo && (
        <div className="lightbox" onClick={closeVideo}>
          <div className="max-w-4xl w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-xl font-semibold">{playingVideo.title}</h3>
              <button onClick={closeVideo} className="text-white text-3xl hover:text-orange-400 transition-colors">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${playingVideo.youtubeId}?autoplay=1`}
                title={playingVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-[50vh] rounded-xl"
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Gallery;
